// src/Studio/timeline/fxRuntime.evaluation.ts
//
// Task T7 of the 2026-04-08 studio file-splits plan. Pure mechanical
// extraction from fxRuntime.ts — no behavior change.
//
// Owns:
//   - FxNodeModifier type + constants (FX_EPSILON, TWO_PI, GPU tuning table)
//   - FxModulationRuntimeState type + modulationRuntimeStateByFxBlockId Map
//   - All modulation functions (resolve, band, latency, response, history, prune)
//   - All keyframe evaluation functions (evaluateFxBlockAtPlayhead, sampleLinear, etc.)
//   - cleanupStaleModulationState helper

import type { FxBlock } from "./fxTypes";
import type { FxRenderPacket, FxAudioFeatures } from "./fxRuntime";
import { clamp01, clamp, toFinite } from "./fxRuntime.shared";

// ── types ────────────────────────────────────────────────────────

export type FxNodeModifier = {
  offsetX: number;
  offsetY: number;
  scaleMultiplier: number;
  rotationOffset: number;
  opacityMultiplier: number;
  globalCompositeOperation?: GlobalCompositeOperation;
};

type FxKeyframeTimeMode = "local" | "absolute";

export type FxBlockFrameEvaluation = {
  opacity: number;
  params: Record<string, number>;
  modulation: FxBlock["modulation"];
  mask: FxBlock["mask"];
  layers: FxBlock["layers"];
};

export type FxModulationRuntimeState = {
  response: number;
  lastSampleSec: number;
  lastResponseSec: number;
  lastSeenSec: number;
  history: Array<{ t: number; v: number }>;
};

// ── constants ────────────────────────────────────────────────────

export const FX_EPSILON = 0.0001;
export const TWO_PI = Math.PI * 2;
const MODULATION_HISTORY_WINDOW_SEC = 1.5;
const MODULATION_STALE_WINDOW_SEC = 4;

export const FX_GPU_MODULE_TUNING: Record<
  string,
  { intensity: number; amplitude: number; speed: number }
> = {
  "gpu.lightLeak": { intensity: 1.05, amplitude: 1.04, speed: 1 },
  "gpu.lightLeakSweep": { intensity: 1.14, amplitude: 1.12, speed: 1.1 },
  "gpu.lightLeakBurst": { intensity: 1.08, amplitude: 1.05, speed: 1.04 },
  "gpu.filmBurn": { intensity: 1.16, amplitude: 1.12, speed: 1.08 },
  "gpu.halation": { intensity: 0.98, amplitude: 0.9, speed: 0.94 },
  "gpu.contrastBloom": { intensity: 1.1, amplitude: 1, speed: 0.95 },
  "gpu.dreamGlow": { intensity: 1.02, amplitude: 1.04, speed: 0.96 },
  "gpu.anamorphicStreak": { intensity: 0.98, amplitude: 1.02, speed: 1.0 },
  "gpu.bloomThreshold": { intensity: 1.1, amplitude: 1, speed: 0.94 },
  "gpu.softFilmFade": { intensity: 1, amplitude: 0.98, speed: 0.9 },
  "gpu.filmGrain": { intensity: 0.98, amplitude: 1.12, speed: 1.12 },
  "gpu.dust": { intensity: 1, amplitude: 1.08, speed: 0.96 },
  "gpu.lensDust": { intensity: 0.96, amplitude: 1.04, speed: 0.9 },
  "gpu.dustOverlay": { intensity: 0.92, amplitude: 1.02, speed: 0.88 },
  "gpu.sparkleDust": { intensity: 1.04, amplitude: 1.1, speed: 1.08 },
  "gpu.haze": { intensity: 0.84, amplitude: 0.82, speed: 0.8 },
  "gpu.bleachBypass": { intensity: 1.1, amplitude: 0.9, speed: 1 },
  "gpu.kodakLutPack": { intensity: 0.98, amplitude: 0.9, speed: 0.9 },
  "gpu.chromaticAberration": { intensity: 1.12, amplitude: 1.1, speed: 1.03 },
  "gpu.chromeGlow": { intensity: 1.02, amplitude: 0.98, speed: 0.92 },
  "gpu.chromaticShift": { intensity: 1.08, amplitude: 1.08, speed: 1.02 },
  "gpu.bokehFlares": { intensity: 1.1, amplitude: 1, speed: 0.95 },
  "gpu.fogRoll": { intensity: 0.92, amplitude: 1.02, speed: 0.86 },
  "gpu.smokeDrift": { intensity: 0.94, amplitude: 1.04, speed: 0.84 },
  "gpu.fogDepth": { intensity: 0.96, amplitude: 1, speed: 0.92 },
  "gpu.rainStreaks": { intensity: 1, amplitude: 1.15, speed: 1.1 },
  "gpu.snow": { intensity: 0.9, amplitude: 1.12, speed: 0.84 },
  "gpu.floatingEmbers": { intensity: 0.96, amplitude: 1.08, speed: 0.9 },
  "gpu.waterDroplets": { intensity: 1, amplitude: 1.04, speed: 0.96 },
  "gpu.heatDistortion": { intensity: 1.06, amplitude: 1.08, speed: 1.06 },
  "gpu.confettiBurst": { intensity: 1.14, amplitude: 1.16, speed: 1.14 },
  "gpu.fireSparks": { intensity: 1.1, amplitude: 1.1, speed: 1.06 },
  "gpu.scratches": { intensity: 1, amplitude: 1.02, speed: 1.06 },
  "gpu.vignette": { intensity: 1.08, amplitude: 0.9, speed: 1.02 },
  "gpu.glitchRgb": { intensity: 1.16, amplitude: 1.16, speed: 1.08 },
  "gpu.glitchTear": { intensity: 1.2, amplitude: 1.14, speed: 1.08 },
  "gpu.ripple": { intensity: 1.04, amplitude: 1.14, speed: 1.05 },
  "gpu.rippleWarp": { intensity: 1.06, amplitude: 1.14, speed: 1.06 },
  "gpu.tempoRipple": { intensity: 1.08, amplitude: 1.16, speed: 1.12 },
  "gpu.motionBlurX": { intensity: 0.98, amplitude: 1.12, speed: 1.06 },
  "gpu.motionBlurY": { intensity: 0.98, amplitude: 1.12, speed: 1.06 },
  "gpu.dataMosh": { intensity: 1.18, amplitude: 1.18, speed: 1.08 },
  "gpu.screenCrack": { intensity: 1.08, amplitude: 1.02, speed: 0.96 },
  "gpu.speedRampBlur": { intensity: 1.04, amplitude: 1.18, speed: 1.06 },
  "gpu.flashWhitePro": { intensity: 1.2, amplitude: 1, speed: 1.14 },
  "gpu.directionalMotionBlur": { intensity: 1.06, amplitude: 1.16, speed: 1.08 },
  "gpu.barrel": { intensity: 1.12, amplitude: 1.1, speed: 1.03 },
  "gpu.twirl": { intensity: 1.08, amplitude: 1.08, speed: 1.04 },
  "gpu.scanline": { intensity: 1.06, amplitude: 1.12, speed: 1.14 },
  "gpu.scanlineJitter": { intensity: 1.1, amplitude: 1.14, speed: 1.16 },
  "gpu.beatScanline": { intensity: 1.14, amplitude: 1.16, speed: 1.2 },
  "gpu.beatRgbPulse": { intensity: 1.14, amplitude: 1.08, speed: 1.18 },
  "gpu.beatParticleBurst": { intensity: 1.2, amplitude: 1.16, speed: 1.22 },
  "gpu.beatLightLeak": { intensity: 1.16, amplitude: 1.06, speed: 1.14 },
  "gpu.barFlash": { intensity: 1.18, amplitude: 1.02, speed: 1.18 },
  "gpu.trebleSparkle": { intensity: 1.1, amplitude: 1.04, speed: 1.2 },
  "gpu.impactShockwave": { intensity: 1.14, amplitude: 1.16, speed: 1.18 },
};

// ── modulation state ─────────────────────────────────────────────

export const modulationRuntimeStateByFxBlockId = new Map<
  string,
  FxModulationRuntimeState
>();

export function cleanupStaleModulationState(
  seenBlockIds: ReadonlySet<string>,
): void {
  for (const id of modulationRuntimeStateByFxBlockId.keys()) {
    if (!seenBlockIds.has(id)) {
      modulationRuntimeStateByFxBlockId.delete(id);
    }
  }
}

// ── modulation functions ─────────────────────────────────────────

export function resolveModulationFactor(
  packet: FxRenderPacket,
  audioFeatures: FxAudioFeatures | undefined,
  playheadSec: number,
): number {
  const mod = packet.modulation;
  if (!mod?.enabled) return 1;

  const bandValue = resolveBandValue(mod.band, audioFeatures);
  const threshold = clamp01(toFinite(mod.threshold, 0.1));
  const strength = clamp01(toFinite(mod.strength, 0.5));
  const smoothing = clamp01(toFinite(mod.smoothing, 0.5));
  const responseMs = clamp(toFinite(mod.responseMs, 90), 0, 600);
  const latencyMs = clamp(toFinite(mod.latencyMs, 0), 0, 250);
  const clampMin = clamp01(toFinite(mod.clampMin, 0));
  const clampMax = clamp01(toFinite(mod.clampMax, 1));
  const clampedBand = clamp(
    bandValue,
    Math.min(clampMin, clampMax),
    Math.max(clampMin, clampMax),
  );

  let driver = 0;
  if (mod.mode === "beat-only") {
    driver = clampedBand >= threshold ? 1 : 0;
  } else {
    driver =
      threshold >= 1 ? 0 : clamp01((clampedBand - threshold) / (1 - threshold));
  }

  const delayedDriver = sampleLatencyCompensatedDriver(
    packet.fxBlockId,
    playheadSec,
    driver,
    latencyMs,
  );
  const responseDriver = applyResponseSmoothing(
    packet.fxBlockId,
    playheadSec,
    delayedDriver,
    responseMs,
  );

  // Apply smoothing as response shaping so modulation can stay punchy or relaxed.
  const shapedDriver = clamp01(
    Math.pow(responseDriver, 1 + smoothing * 1.8),
  );

  // Blend around neutral 1x so modulation can raise/lower intensity smoothly.
  return clamp((1 - strength) + strength * shapedDriver, 0, 1.5);
}

function resolveBandValue(
  band: FxBlock["modulation"]["band"],
  audioFeatures?: FxAudioFeatures,
): number {
  if (!audioFeatures) return 0;
  if (band === "low") return clamp01(audioFeatures.low);
  if (band === "mid") return clamp01(audioFeatures.mid);
  if (band === "high") return clamp01(audioFeatures.high);
  if (band === "onset") return clamp01(audioFeatures.onset);
  if (band === "transient") return clamp01(audioFeatures.transient);
  if (band === "beat") return clamp01(audioFeatures.beat);
  return clamp01(audioFeatures.full);
}

function sampleLatencyCompensatedDriver(
  fxBlockId: string,
  playheadSec: number,
  driver: number,
  latencyMs: number,
): number {
  const state = getOrCreateModulationState(fxBlockId, playheadSec);
  if (playheadSec + 0.4 < state.lastSampleSec) {
    state.history = [];
    state.response = 0;
  }

  state.history.push({ t: playheadSec, v: driver });
  state.lastSampleSec = playheadSec;
  state.lastSeenSec = playheadSec;

  const cutoff = playheadSec - MODULATION_HISTORY_WINDOW_SEC;
  while (state.history.length > 1 && state.history[0].t < cutoff) {
    state.history.shift();
  }

  if (latencyMs <= 0.5) return driver;
  const delayedTime = playheadSec - latencyMs / 1000;
  const delayedSample = sampleHistoryAt(state.history, delayedTime);
  return delayedSample ?? driver;
}

function applyResponseSmoothing(
  fxBlockId: string,
  playheadSec: number,
  driver: number,
  responseMs: number,
): number {
  const state = getOrCreateModulationState(fxBlockId, playheadSec);
  const dt = Math.max(
    0,
    Math.min(0.25, playheadSec - state.lastResponseSec),
  );
  state.lastResponseSec = playheadSec;
  state.lastSeenSec = playheadSec;

  if (responseMs <= 0.5) {
    state.response = driver;
    maybePruneStaleModulationState(playheadSec);
    return state.response;
  }

  const alpha = 1 - Math.exp((-dt * 1000) / Math.max(1, responseMs));
  state.response = state.response + (driver - state.response) * alpha;
  maybePruneStaleModulationState(playheadSec);
  return clamp01(state.response);
}

function getOrCreateModulationState(
  fxBlockId: string,
  playheadSec: number,
): FxModulationRuntimeState {
  const existing = modulationRuntimeStateByFxBlockId.get(fxBlockId);
  if (existing) return existing;
  const created: FxModulationRuntimeState = {
    response: 0,
    lastSampleSec: playheadSec,
    lastResponseSec: playheadSec,
    lastSeenSec: playheadSec,
    history: [],
  };
  modulationRuntimeStateByFxBlockId.set(fxBlockId, created);
  return created;
}

function sampleHistoryAt(
  history: Array<{ t: number; v: number }>,
  sampleTime: number,
): number | null {
  if (history.length === 0) return null;
  if (sampleTime <= history[0].t) return history[0].v;
  const last = history[history.length - 1];
  if (sampleTime >= last.t) return last.v;

  for (let i = 1; i < history.length; i += 1) {
    const prev = history[i - 1];
    const next = history[i];
    if (sampleTime > next.t) continue;
    const span = Math.max(0.0001, next.t - prev.t);
    const t = clamp01((sampleTime - prev.t) / span);
    return prev.v + (next.v - prev.v) * t;
  }
  return last.v;
}

function maybePruneStaleModulationState(
  currentPlayheadSec: number,
): void {
  if (modulationRuntimeStateByFxBlockId.size <= 256) return;
  const cutoff = currentPlayheadSec - MODULATION_STALE_WINDOW_SEC;
  for (const [fxBlockId, state] of modulationRuntimeStateByFxBlockId.entries()) {
    if (state.lastSeenSec < cutoff) {
      modulationRuntimeStateByFxBlockId.delete(fxBlockId);
    }
  }
}

// ── keyframe evaluation ──────────────────────────────────────────

export function evaluateFxBlockAtPlayhead(
  block: FxBlock,
  playhead: number,
): FxBlockFrameEvaluation {
  const params: Record<string, number> = { ...(block.params || {}) };
  const modulation: FxBlock["modulation"] = { ...block.modulation };
  const mask: FxBlock["mask"] = { ...block.mask };
  const layers: FxBlock["layers"] = Array.isArray(block.layers)
    ? block.layers.map((layer) => ({ ...layer }))
    : [];
  let opacity = clamp01(block.opacity);

  const keyframes = Array.isArray(block.keyframes) ? block.keyframes : [];
  if (keyframes.length === 0) {
    return { opacity, params, modulation, mask, layers };
  }

  const duration = Math.max(0.001, block.end - block.start);
  const mode = inferFxKeyframeTimeMode(keyframes, duration);
  const sampleTime =
    mode === "absolute" ? playhead : Math.max(0, playhead - block.start);

  const keyframesByProp = new Map<
    string,
    Array<{ time: number; value: number }>
  >();
  for (const kf of keyframes) {
    if (!kf || typeof kf.property !== "string") continue;
    const value = toFinite(kf.value, Number.NaN);
    if (!Number.isFinite(value)) continue;
    const time = toFinite(kf.time, Number.NaN);
    if (!Number.isFinite(time)) continue;
    const prop = kf.property.trim();
    if (!prop) continue;
    const existing = keyframesByProp.get(prop);
    if (existing) {
      existing.push({ time, value });
    } else {
      keyframesByProp.set(prop, [{ time, value }]);
    }
  }

  for (const [property, propertyKeyframes] of keyframesByProp.entries()) {
    if (!propertyKeyframes.length) continue;
    propertyKeyframes.sort((a, b) => a.time - b.time);
    const nextValue = sampleLinear(propertyKeyframes, sampleTime);
    applyEvaluatedFxProperty({
      property,
      value: nextValue,
      params,
      modulation,
      mask,
      layers,
      setOpacity: (nextOpacity) => {
        opacity = clamp01(nextOpacity);
      },
    });
  }

  return { opacity, params, modulation, mask, layers };
}

function inferFxKeyframeTimeMode(
  keyframes: FxBlock["keyframes"],
  duration: number,
): FxKeyframeTimeMode {
  if (!keyframes.length) return "local";
  let outOfLocalRange = 0;
  for (const kf of keyframes) {
    const t = toFinite(kf?.time, Number.NaN);
    if (!Number.isFinite(t)) continue;
    if (t > duration + 0.001) outOfLocalRange += 1;
  }
  return outOfLocalRange > keyframes.length / 2 ? "absolute" : "local";
}

function sampleLinear(
  keyframes: Array<{ time: number; value: number }>,
  sampleTime: number,
): number {
  if (keyframes.length === 1) return keyframes[0].value;
  if (sampleTime <= keyframes[0].time) return keyframes[0].value;
  const last = keyframes[keyframes.length - 1];
  if (sampleTime >= last.time) return last.value;
  for (let i = 1; i < keyframes.length; i += 1) {
    const prev = keyframes[i - 1];
    const next = keyframes[i];
    if (sampleTime > next.time) continue;
    const span = Math.max(0.0001, next.time - prev.time);
    const t = clamp01((sampleTime - prev.time) / span);
    return prev.value + (next.value - prev.value) * t;
  }
  return last.value;
}

function applyEvaluatedFxProperty(args: {
  property: string;
  value: number;
  params: Record<string, number>;
  modulation: FxBlock["modulation"];
  mask: FxBlock["mask"];
  layers: FxBlock["layers"];
  setOpacity: (nextOpacity: number) => void;
}): void {
  const { property, value, params, modulation, mask, layers, setOpacity } =
    args;
  const normalized = property.trim();
  const lower = normalized.toLowerCase();
  if (lower === "opacity") {
    setOpacity(clamp01(value));
    return;
  }
  if (lower.startsWith("params.")) {
    const paramName = normalized.slice("params.".length).trim();
    if (paramName) params[paramName] = value;
    return;
  }
  if (
    lower === "intensity" ||
    lower === "amplitude" ||
    lower === "speed" ||
    lower === "blurradius"
  ) {
    params[normalized] = value;
    return;
  }
  const layerMatch = lower.match(/^layers\.(\d+)\.([a-z0-9]+)$/);
  if (layerMatch) {
    const layer = layers[Number(layerMatch[1])];
    const key = layerMatch[2];
    if (layer && layer.type === "overlay-video") {
      if (key === "positionx") layer.positionX = clamp(value, -4000, 4000);
      else if (key === "positiony")
        layer.positionY = clamp(value, -4000, 4000);
      else if (key === "positionz") layer.positionZ = clamp(value, -400, 400);
      else if (key === "opacity") layer.opacity = clamp01(value);
      else if (key === "playbackrate")
        layer.playbackRate = clamp(value, 0.1, 4);
      else if (key === "startoffset") layer.startOffset = Math.max(0, value);
      else if (key === "duration") layer.duration = Math.max(0.001, value);
      else if (key === "loop") layer.loop = value >= 0.5;
    }
    return;
  }
  if (lower === "modulation.strength") {
    modulation.strength = clamp01(value);
    return;
  }
  if (lower === "modulation.threshold") {
    modulation.threshold = clamp01(value);
    return;
  }
  if (lower === "modulation.smoothing") {
    modulation.smoothing = clamp01(value);
    return;
  }
  if (lower === "modulation.responsems") {
    modulation.responseMs = clamp(value, 0, 600);
    return;
  }
  if (lower === "modulation.latencyms") {
    modulation.latencyMs = clamp(value, 0, 250);
    return;
  }
  if (lower === "modulation.clampmin") {
    modulation.clampMin = clamp01(value);
    return;
  }
  if (lower === "modulation.clampmax") {
    modulation.clampMax = clamp01(value);
    return;
  }
  if (lower === "mask.opacity") {
    mask.opacity = clamp01(value);
    return;
  }
  if (lower === "mask.featherpx") {
    mask.featherPx = clamp(value, 0, 300);
    return;
  }
  if (lower === "mask.expandpx") {
    mask.expandPx = clamp(value, -500, 500);
    return;
  }
  if (lower === "mask.invert") {
    mask.invert = value >= 0.5;
  }
}

// ── FX cost estimation ──────────────────────────────────────────

const FX_COST_BASE_CPU_MS = 0.5;
const FX_COST_BASE_GPU_MS = 1.5;

/**
 * Rough per-block cost estimate in milliseconds. Used by the budget
 * enforcer to decide which blocks to drop when the total exceeds
 * the per-frame FX budget.
 */
export function resolveFxBlockCostMs(
  renderPath: "cpu-transform" | "gpu-module",
  targetCount: number,
): number {
  const base = renderPath === "gpu-module" ? FX_COST_BASE_GPU_MS : FX_COST_BASE_CPU_MS;
  return base * Math.max(1, targetCount);
}
